import { useMemo, useState, type MouseEvent } from 'react';
import {
  Box, Chip, IconButton, InputAdornment, Menu, MenuItem, ListItemIcon, ListItemText,
  Paper, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { Edit, MoreVert, Search, Visibility, VisibilityOff } from '@mui/icons-material';
import type { RentalZoneSummary } from '../../services/rentalManagementService';
import {
  buildRentalZoneHierarchy,
  filterPrimaryZonesForSearch,
  type RentalZoneHierarchyRow,
} from '../../utils/rentalPackages';

interface ZoneMeta {
  _id: string;
  primaryZoneId?: string;
  zoneLevel?: string;
}

interface Props {
  rentalZones: RentalZoneSummary[];
  zoneMeta: ZoneMeta[];
  isLoading?: boolean;
  onEditRentalFare: (zoneCode: string) => void;
  onViewPackages?: (zoneCode: string) => void;
}

function ZoneRowCells({
  zone,
  serial,
  indent = false,
}: {
  zone: RentalZoneHierarchyRow;
  serial: number;
  indent?: boolean;
}) {
  return (
    <>
      <TableCell>{serial}</TableCell>
      <TableCell>
        <Box sx={{ pl: indent ? 3 : 0 }}>
          <Typography variant="body2" fontWeight={700}>{zone.name}</Typography>
          <Typography variant="caption" color="text.secondary">{zone.code}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={zone.zoneLevel === 'PRIMARY' ? 'Primary' : 'Secondary'}
          color={zone.zoneLevel === 'PRIMARY' ? 'primary' : 'default'}
          variant="outlined"
        />
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={zone.isActive ? 'Active' : 'Inactive'}
          color={zone.isActive ? 'success' : 'default'}
        />
      </TableCell>
    </>
  );
}

export default function RentalZoneHierarchyTable({
  rentalZones,
  zoneMeta,
  isLoading = false,
  onEditRentalFare,
  onViewPackages,
}: Props) {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [expandedPrimaryIds, setExpandedPrimaryIds] = useState<Set<string>>(() => new Set());
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuZone, setMenuZone] = useState<RentalZoneHierarchyRow | null>(null);

  const { primaryZones, secondariesByPrimaryId } = useMemo(
    () => buildRentalZoneHierarchy(rentalZones, zoneMeta),
    [rentalZones, zoneMeta],
  );

  const filteredPrimaries = useMemo(
    () => filterPrimaryZonesForSearch(primaryZones, secondariesByPrimaryId, search),
    [primaryZones, secondariesByPrimaryId, search],
  );

  const paginatedPrimaries = useMemo(
    () => filteredPrimaries.slice(page * pageSize, page * pageSize + pageSize),
    [filteredPrimaries, page, pageSize],
  );

  const toggleExpanded = (primaryId: string) => {
    setExpandedPrimaryIds((prev) => {
      const next = new Set(prev);
      if (next.has(primaryId)) next.delete(primaryId);
      else next.add(primaryId);
      return next;
    });
  };

  const openMenu = (event: MouseEvent<HTMLElement>, zone: RentalZoneHierarchyRow) => {
    setMenuAnchor(event.currentTarget);
    setMenuZone(zone);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuZone(null);
  };

  const renderActions = (zone: RentalZoneHierarchyRow, options: { isPrimary: boolean; hasSecondaries: boolean }) => {
    const expanded = expandedPrimaryIds.has(zone._id);
    const showExpand = options.isPrimary && options.hasSecondaries;

    return (
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={0.25}>
        {showExpand ? (
          <Tooltip title={expanded ? 'Hide secondary zones' : 'Show secondary zones'}>
            <IconButton size="small" onClick={() => toggleExpanded(zone._id)}>
              {expanded ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="View packages">
            <IconButton size="small" onClick={() => onViewPackages?.(zone.code)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="More actions">
          <IconButton size="small" onClick={(e: MouseEvent<HTMLElement>) => openMenu(e, zone)}>
            <MoreVert fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  };

  let serial = page * pageSize;

  return (
    <>
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2.5, border: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          placeholder="Search primary or secondary zones…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280 }}
        />
      </Paper>

      {isLoading ? (
        <Stack spacing={1}>{[1, 2, 3, 4].map((i) => <Skeleton key={i} height={48} />)}</Stack>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 56 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Zone</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Level</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 108 }} align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedPrimaries.map((primary) => {
                  serial += 1;
                  const rowSerial = serial;
                  const secondaries = secondariesByPrimaryId.get(primary._id) || [];
                  const hasSecondaries = secondaries.length > 0;
                  const expanded = expandedPrimaryIds.has(primary._id);
                  const visibleSecondaries = expanded
                    ? secondaries.filter((child) => {
                      const term = search.trim().toLowerCase();
                      if (!term) return true;
                      return child.name.toLowerCase().includes(term)
                        || child.code.toLowerCase().includes(term)
                        || primary.name.toLowerCase().includes(term)
                        || primary.code.toLowerCase().includes(term);
                    })
                    : [];

                  return [
                    <TableRow
                      key={primary._id}
                      hover
                      sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
                    >
                      <ZoneRowCells zone={primary} serial={rowSerial} />
                      <TableCell align="right">
                        {renderActions(primary, { isPrimary: true, hasSecondaries })}
                      </TableCell>
                    </TableRow>,
                    ...visibleSecondaries.map((secondary) => {
                      serial += 1;
                      return (
                        <TableRow
                          key={secondary._id}
                          hover
                          sx={{
                            bgcolor: alpha(theme.palette.action.hover, 0.35),
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                          }}
                        >
                          <ZoneRowCells zone={secondary} serial={serial} indent />
                          <TableCell align="right">
                            {renderActions(secondary, { isPrimary: false, hasSecondaries: false })}
                          </TableCell>
                        </TableRow>
                      );
                    }),
                  ];
                })}
                {!paginatedPrimaries.length && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No primary zones found. Create zones first under Zone Management.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredPrimaries.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            if (menuZone) onEditRentalFare(menuZone.code);
            closeMenu();
          }}
        >
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          <ListItemText>Edit rental fare</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
